import { useEffect, useState } from "react";
import {
    ArrowRight,
    ChevronDown,
    Download,
    MessageCircle,
    Check,
} from "lucide-react";
import { HERO_SLIDES, BRAND } from "../data/site";
import { useCatalog } from "../context/CatalogContext";

const TRUST = [
    "Farm-Direct Sourcing",
    "Export Ready",
    "Quality Assured",
    "Global Shipping",
];

const STATS = [
    { v: "100+", k: "Farming Partners" },
    { v: "25+", k: "Product Variants" },
    { v: "Full", k: "Export Documentation" },
    { v: "Fast", k: "Global Shipping" },
];

export default function Hero() {
    const [idx, setIdx] = useState(0);
    const { open } = useCatalog();

    useEffect(() => {
        const t = setInterval(
            () => setIdx((i) => (i + 1) % HERO_SLIDES.length),
            6000
        );
        return () => clearInterval(t);
    }, []);

    return (
        <section
            id="top"
            data-testid="hero-section"
            className="relative isolate min-h-[100svh] w-full overflow-hidden bg-black text-white"
        >
            {/* Cinematic rotating background */}
            {HERO_SLIDES.map((s, i) => (
                <div
                    key={s.url}
                    aria-hidden={i !== idx}
                    className={`absolute inset-0 transition-opacity duration-[1600ms] ease-out ${
                        i === idx ? "opacity-100" : "opacity-0"
                    }`}
                >
                    <img
                        src={s.url}
                        alt={s.alt}
                        loading={i === 0 ? "eager" : "lazy"}
                        decoding="async"
                        className={`h-full w-full object-cover ${
                            i === idx ? "animate-slow-zoom" : ""
                        }`}
                    />
                </div>
            ))}

            {/* Dark cinematic overlay (top 0.65 → bottom 0.55, plus side vignette for left-aligned text) */}
            <div
                className="absolute inset-0"
                style={{
                    background:
                        "linear-gradient(rgba(0,0,0,0.65), rgba(0,0,0,0.55))",
                }}
            />
            <div className="absolute inset-0 hidden bg-gradient-to-r from-black/55 via-black/25 to-transparent lg:block" />

            {/* Content */}
            <div className="relative mx-auto flex min-h-[100svh] max-w-7xl flex-col justify-end px-5 pb-16 pt-32 sm:px-8 sm:pb-20 lg:px-10 lg:pb-24">
                <div className="mx-auto w-full max-w-[700px] text-center lg:mx-0 lg:text-left animate-fade-up" style={{ animationDelay: "60ms" }}>
                    <span
                        data-testid="hero-kicker"
                        className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-3 py-1 text-[11px] font-medium uppercase tracking-[0.22em] text-white/95 backdrop-blur"
                    >
                        <span className="h-1.5 w-1.5 rounded-full bg-brand-turmeric" />
                        {HERO_SLIDES[idx].kicker}
                    </span>

                    <h1
                        data-testid="hero-headline"
                        className="font-display text-[40px] font-bold leading-[1.02] tracking-[-0.03em] sm:text-6xl lg:text-[78px]"
                        style={{
                            textShadow: "0 4px 30px rgba(0,0,0,0.45)",
                        }}
                    >
                        Premium Indian Spices
                        <br />
                        <span className="rp-text-gradient">Exported Worldwide.</span>
                    </h1>

                    <p
                        data-testid="hero-subhead"
                        className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-white/90 sm:text-lg lg:mx-0"
                    >
                        Directly sourced from India&apos;s spice capital, Guntur.
                        Delivering premium red chilli, turmeric, cumin and coriander
                        to global importers and distributors.
                    </p>

                    {/* Trust metrics */}
                    <ul
                        data-testid="hero-trust"
                        className="mx-auto mt-7 flex max-w-2xl flex-wrap items-center justify-center gap-x-5 gap-y-2 lg:mx-0 lg:justify-start"
                    >
                        {TRUST.map((t) => (
                            <li
                                key={t}
                                className="inline-flex items-center gap-1.5 text-[13px] font-medium text-white/95"
                            >
                                <span className="grid h-4 w-4 place-items-center rounded-full bg-brand-turmeric text-black">
                                    <Check className="h-2.5 w-2.5" strokeWidth={3} />
                                </span>
                                {t}
                            </li>
                        ))}
                    </ul>

                    {/* CTA buttons */}
                    <div
                        data-testid="hero-ctas"
                        className="mt-9 flex flex-wrap items-center justify-center gap-3 lg:justify-start"
                    >
                        <a
                            href="#contact"
                            data-testid="hero-cta-quote"
                            className="group inline-flex items-center gap-2 rounded-full bg-white px-6 py-3.5 text-sm font-semibold text-ink shadow-xl shadow-black/30 transition-transform hover:scale-[1.04]"
                        >
                            Get Export Quote
                            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                        </a>
                        <button
                            type="button"
                            onClick={open}
                            data-testid="hero-cta-catalog"
                            className="inline-flex items-center gap-2 rounded-full bg-brand-red px-6 py-3.5 text-sm font-semibold text-white shadow-xl shadow-black/30 transition-transform hover:scale-[1.04]"
                        >
                            <Download className="h-4 w-4" />
                            Download Product Catalog
                        </button>
                        <a
                            href={`https://wa.me/${BRAND.leadWhatsapp}?text=${encodeURIComponent(
                                "Hello RP Traders, I would like to enquire about your export products."
                            )}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="hero-cta-whatsapp"
                            className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-6 py-3.5 text-sm font-semibold text-white shadow-xl shadow-black/30 transition-transform hover:scale-[1.04]"
                        >
                            <MessageCircle className="h-4 w-4" />
                            Chat on WhatsApp
                        </a>
                    </div>
                </div>

                {/* Stats strip */}
                <div
                    data-testid="hero-stats"
                    className="mt-16 grid w-full grid-cols-2 gap-px overflow-hidden rounded-2xl border border-white/15 bg-white/10 backdrop-blur-md lg:grid-cols-4 animate-fade-up"
                    style={{ animationDelay: "240ms" }}
                >
                    {STATS.map((s) => (
                        <div
                            key={s.k}
                            className="bg-black/30 px-5 py-5 text-center sm:py-6 lg:text-left"
                        >
                            <div className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl">
                                {s.v}
                            </div>
                            <div className="mt-1 text-[12px] uppercase tracking-[0.18em] text-white/75">
                                {s.k}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Dots + thumbnail slidebar */}
                <div className="mt-8 flex flex-col gap-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            {HERO_SLIDES.map((_, i) => (
                                <button
                                    key={i}
                                    onClick={() => setIdx(i)}
                                    data-testid={`hero-dot-${i}`}
                                    aria-label={`Slide ${i + 1}`}
                                    className={`h-1.5 rounded-full transition-all ${
                                        i === idx
                                            ? "w-9 bg-white"
                                            : "w-4 bg-white/40 hover:bg-white/70"
                                    }`}
                                />
                            ))}
                        </div>
                        <a
                            href="#carousel"
                            className="hidden items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/70 hover:text-white sm:inline-flex"
                        >
                            Scroll <ChevronDown className="h-4 w-4 animate-bounce" />
                        </a>
                    </div>

                    {/* Thumbnail strip — click to jump to that slide */}
                    <div
                        data-testid="hero-thumb-strip"
                        className="no-scrollbar flex gap-3 overflow-x-auto pb-1"
                    >
                        {HERO_SLIDES.map((s, i) => (
                            <button
                                key={s.url}
                                onClick={() => setIdx(i)}
                                data-testid={`hero-thumb-${i}`}
                                aria-label={`Open slide ${i + 1}`}
                                className={`relative h-16 w-24 shrink-0 overflow-hidden rounded-xl border transition-all sm:h-20 sm:w-32 ${
                                    i === idx
                                        ? "border-brand-turmeric ring-2 ring-brand-turmeric/60 scale-[1.04]"
                                        : "border-white/20 opacity-65 hover:opacity-100"
                                }`}
                            >
                                <img
                                    src={s.url}
                                    alt={s.alt}
                                    loading="lazy"
                                    className="h-full w-full object-cover"
                                />
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}
